import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import useAuthenticateMutation from './queries/useAuthenticateMutation';
import useLoginUrlContext from './useLoginUrlContext';
import buildAuthSuccessRedirectUrl from './buildAuthSuccessRedirectUrl';
import buildMfaUrl from './buildMfaUrl';
import { subscriptionErrorParamName } from './Login.constants';
import getLoginErrorFromResponse, {
  type LoginErrorResult,
} from './getLoginErrorFromResponse';
import {
  AuthenticateGrantType,
  AuthenticateScope,
  AuthenticateStatus,
  type AuthenticateApiError,
  type AuthenticateRequest,
  type AuthenticateResponse,
} from '@/services/login.types';
import { Location } from '@/utils/windowsUtils';
import { getOrCreateDeviceId } from '@/utils/deviceId';
import type { FormContextParts } from '@/components/forms/form/Form.types.ts';

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
  registrationToken?: string;
}

type SetError = FormContextParts<LoginFormValues>['setError'];

const useLoginSubmit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pkce, appContext } = useLoginUrlContext();
  const [, setSearchParams] = useSearchParams();
  // Notice (subscription / billing with links): kept as standalone state
  // because it is a ReactNode with actions, not a plain field error. The
  // credential error lives in RHF (set via setError below), not in state.
  const [noticeMessage, setNoticeMessage] = useState<ReactNode>();
  const [isDesktopServiceProviderUser, setIsDesktopServiceProviderUser] =
    useState(false);

  useEffect(() => {
    setSearchParams((params) => {
      params.delete(subscriptionErrorParamName);
      return params;
    });
  }, [setSearchParams]);

  const clearErrors = () => {
    setNoticeMessage(undefined);
    setIsDesktopServiceProviderUser(false);
  };

  const setCredentialError = (setError: SetError, message: string) => {
    setError('email', { type: 'manual', message: '' });
    setError('password', { type: 'manual', message });
  };

  const applyLoginError = (result: LoginErrorResult, setError: SetError) => {
    if (result.kind === 'service-provider') {
      setIsDesktopServiceProviderUser(true);
      return;
    }
    if (result.kind === 'notice') {
      setNoticeMessage(result.message);
      return;
    }
    setCredentialError(setError, result.message);
  };

  const routeAuthResponse = (
    data: AuthenticateResponse,
    variables: AuthenticateRequest,
    setError: SetError,
  ) => {
    if (data.status === AuthenticateStatus.MFA_CHALLENGE) {
      if (!pkce) {
        setCredentialError(
          setError,
          t('pageLogin.error_AUTHENTICATION_FAILED'),
        );
        return;
      }
      navigate(
        buildMfaUrl({
          pkce,
          appContext,
          mfaCode: data.mfaCode,
          mfaCodeSentTo: data.mfaCodeSentTo,
          mfaType: data.mfaType,
          grantType: variables.grantType,
        }),
      );
      return;
    }

    if (data.status === AuthenticateStatus.SUCCESSFUL) {
      if (!pkce) {
        setCredentialError(
          setError,
          t('pageLogin.error_AUTHENTICATION_FAILED'),
        );
        return;
      }
      Location.change(
        buildAuthSuccessRedirectUrl({
          redirectUri: pkce.redirectUri,
          code: data.code,
          state: data.state,
          requestedPath: appContext.requestedPath,
          grantType: variables.grantType,
        }),
      );
      return;
    }

    applyLoginError(getLoginErrorFromResponse({ data, t }), setError);
  };

  const mutation = useAuthenticateMutation();

  const handleSubmitLogin = async (
    values: LoginFormValues,
    _event: unknown,
    {
      setError,
      clearErrors: clearFieldErrors,
    }: FormContextParts<LoginFormValues>,
  ) => {
    clearErrors();
    clearFieldErrors(['email', 'password']);

    if (!pkce) {
      setCredentialError(setError, t('pageLogin.error_AUTHENTICATION_FAILED'));
      return;
    }

    if (values.rememberMe) {
      localStorage.setItem('remember_me', 'true');
    } else {
      localStorage.removeItem('remember_me');
    }

    // TODO: branch on values.registrationToken (register-then-login flow) — out of scope for this PR.

    const body: AuthenticateRequest = {
      login: values.email,
      password: values.password,
      grantType: values.rememberMe
        ? AuthenticateGrantType.REFRESH_TOKEN
        : AuthenticateGrantType.AUTHORIZATION_CODE,
      scope: AuthenticateScope.FULL,
      clientId: pkce.clientId,
      redirectUri: pkce.redirectUri,
      state: pkce.state,
      b64CodeChallenge: pkce.b64CodeChallenge,
      codeChallengeMethod: pkce.codeChallengeMethod,
      deviceId: getOrCreateDeviceId(),
    };

    try {
      const data = await mutation.mutateAsync(body);
      routeAuthResponse(data, body, setError);
    } catch (error) {
      const axiosError = error as AxiosError<AuthenticateApiError> | undefined;
      applyLoginError(
        getLoginErrorFromResponse({ data: axiosError?.response?.data, t }),
        setError,
      );
    }
  };

  return {
    handleSubmitLogin,
    clearErrors,
    noticeMessage,
    isDesktopServiceProviderUser,
    isSubmitting: mutation.isPending,
  };
};

export default useLoginSubmit;
